import { useState, useEffect, useRef } from "react";

type Kind = "bug" | "feature" | "question";
type View = "form" | "thread" | "list";
type Msg = { senderType: string; message: string; createdAt?: string };
type Ticket = { id: number; subject: string; status: string; agentState?: string; category?: string };

// Floating report widget AND mini support-chat: file a bug/feature/question with an auto
// screenshot, then see the agent's replies and talk back — the Claude ticket-loop picks up
// each tester reply within ~5 min and continues the conversation.
export default function ReportWidget() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>("form");
  const [kind, setKind] = useState<Kind>("bug");
  const [message, setMessage] = useState("");
  const [withShot, setWithShot] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ticketId, setTicketId] = useState<number | null>(null);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [reply, setReply] = useState("");
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const pollRef = useRef<any>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Poll the open ticket's thread for new agent replies while it's showing.
  useEffect(() => {
    if (view !== "thread" || !ticketId) { clearInterval(pollRef.current); return; }
    const load = async () => {
      try {
        const r = await fetch(`/api/support/tickets/${ticketId}`, { credentials: "include" });
        if (r.ok) { const t = await r.json(); setMsgs(t.messages || []); }
      } catch {}
    };
    load();
    pollRef.current = setInterval(load, 12000);
    return () => clearInterval(pollRef.current);
  }, [view, ticketId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  async function loadTickets() {
    try { const r = await fetch("/api/support/tickets", { credentials: "include" }); if (r.ok) setTickets(await r.json()); } catch {}
  }

  async function submit() {
    if (!message.trim()) { setErr("Tell us what's happening first."); return; }
    setBusy(true); setErr("");
    let screenshot: string | null = null;
    if (withShot) {
      try {
        const html2canvas = (await import("html2canvas")).default;
        const canvas = await html2canvas(document.body, { logging: false, useCORS: true, scale: 0.6 });
        screenshot = canvas.toDataURL("image/jpeg", 0.7);
      } catch {}
    }
    try {
      const subject = `[${kind}] ${message.trim().slice(0, 60)}`;
      const res = await fetch("/api/support/tickets", {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ subject, description: message.trim(), category: kind, priority: kind === "bug" ? "high" : "medium", screenshot, pageUrl: window.location.href }),
      });
      if (!res.ok) throw new Error();
      const t = await res.json();
      setMessage(""); setMsgs([{ senderType: "user", message: message.trim() }]); setTicketId(t.id); setView("thread");
    } catch { setErr("Couldn't file that — try again."); }
    finally { setBusy(false); }
  }

  async function sendReply() {
    if (!reply.trim() || !ticketId) return;
    const text = reply.trim(); setReply("");
    setMsgs((m) => [...m, { senderType: "user", message: text }]);
    try { await fetch(`/api/support/tickets/${ticketId}/messages`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ message: text }) }); } catch {}
  }

  const C = { panel: "#111827", border: "#374151", text: "#e5e7eb", sub: "#9ca3af", accent: "#60a5fa", input: "#1f2937" };

  return (
    <>
      <button onClick={() => { setOpen((v) => !v); if (!open) setView("form"); }} aria-label="Report or view tickets"
        style={{ position: "fixed", right: 20, bottom: 20, zIndex: 9999, width: 52, height: 52, borderRadius: 26, background: C.accent, color: "#0b1220", border: "none", boxShadow: "0 4px 14px rgba(0,0,0,.25)", cursor: "pointer", fontSize: 22, fontWeight: 700 }}
        title="Report a bug / feature / question">{open ? "×" : "?"}</button>

      {open && (
        <div style={{ position: "fixed", right: 20, bottom: 84, zIndex: 9999, width: 340, maxHeight: "70vh", display: "flex", flexDirection: "column", background: C.panel, color: C.text, border: `1px solid ${C.border}`, borderRadius: 12, boxShadow: "0 10px 30px rgba(0,0,0,.4)", fontFamily: "system-ui, sans-serif" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{view === "thread" ? `Ticket #${ticketId}` : view === "list" ? "Your tickets" : "Report to the team"}</div>
            <button onClick={() => { setView("list"); loadTickets(); }} style={{ background: "transparent", border: "none", color: C.sub, cursor: "pointer", fontSize: 12 }}>My tickets</button>
          </div>

          {view === "form" && (
            <div style={{ padding: 14 }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                {(["bug", "feature", "question"] as Kind[]).map((k) => (
                  <button key={k} onClick={() => setKind(k)} style={{ flex: 1, padding: "6px 4px", borderRadius: 8, cursor: "pointer", fontSize: 12, textTransform: "capitalize", background: kind === k ? C.accent : "transparent", color: kind === k ? "#0b1220" : C.sub, border: `1px solid ${kind === k ? C.accent : C.border}` }}>{k}</button>
                ))}
              </div>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder={kind === "bug" ? "What broke? What did you expect?" : kind === "feature" ? "What would you like it to do?" : "What's your question?"}
                style={{ width: "100%", background: C.input, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, padding: 8, fontSize: 13, resize: "vertical", boxSizing: "border-box" }} />
              <label style={{ display: "flex", alignItems: "center", gap: 6, margin: "8px 0", fontSize: 12, color: C.sub }}>
                <input type="checkbox" checked={withShot} onChange={(e) => setWithShot(e.target.checked)} /> Attach a screenshot of this screen
              </label>
              {err && <div style={{ color: "#f87171", fontSize: 12, marginBottom: 6 }}>{err}</div>}
              <button onClick={submit} disabled={busy} style={{ width: "100%", padding: 9, borderRadius: 8, background: busy ? C.border : C.accent, color: busy ? C.sub : "#0b1220", border: "none", fontWeight: 600, cursor: busy ? "default" : "pointer", fontSize: 14 }}>{busy ? "Filing…" : "Send to the team"}</button>
            </div>
          )}

          {view === "list" && (
            <div style={{ padding: 10, overflowY: "auto" }}>
              <button onClick={() => setView("form")} style={{ width: "100%", padding: 8, marginBottom: 8, borderRadius: 8, background: C.accent, color: "#0b1220", border: "none", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>+ New report</button>
              {tickets.length === 0 && <div style={{ color: C.sub, fontSize: 13, padding: 8 }}>No tickets yet.</div>}
              {tickets.map((t) => (
                <button key={t.id} onClick={() => { setTicketId(t.id); setView("thread"); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", marginBottom: 6, borderRadius: 8, background: C.input, color: C.text, border: `1px solid ${C.border}`, cursor: "pointer", fontSize: 12 }}>
                  <span style={{ color: C.sub }}>#{t.id}</span> {t.subject.slice(0, 40)}
                  <span style={{ float: "right", fontSize: 10, color: t.status === "resolved" ? "#34d399" : C.accent }}>{t.status}</span>
                </button>
              ))}
            </div>
          )}

          {view === "thread" && (
            <>
              <div style={{ flex: 1, overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                {msgs.map((m, i) => (
                  <div key={i} style={{ alignSelf: m.senderType === "user" ? "flex-end" : "flex-start", maxWidth: "85%", padding: "8px 10px", borderRadius: 10, fontSize: 13, lineHeight: 1.45,
                    background: m.senderType === "user" ? C.accent : C.input, color: m.senderType === "user" ? "#0b1220" : C.text, border: m.senderType === "user" ? "none" : `1px solid ${C.border}` }}>
                    {m.senderType !== "user" && <div style={{ fontSize: 10, color: C.accent, marginBottom: 2, fontWeight: 600 }}>Agent</div>}
                    {m.message}
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <div style={{ display: "flex", gap: 6, padding: 10, borderTop: `1px solid ${C.border}` }}>
                <input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()} placeholder="Reply…"
                  style={{ flex: 1, background: C.input, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 10px", fontSize: 13 }} />
                <button onClick={sendReply} style={{ padding: "8px 12px", borderRadius: 8, background: C.accent, color: "#0b1220", border: "none", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>Send</button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
